import type { DgCoverageEntry } from './guide-coverage.ts';

/** `"A"`, `"A" and "B"`, `"A", "B" and "C"`. */
const quotedList = (texts: readonly string[]): string => {
  const quoted = texts.map((text) => `"${text}"`);

  return quoted.length <= 1
    ? (quoted[0] ?? '')
    : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
};

/**
 * What "Help me fix this" sends, in the researcher's voice: the research questions the check found
 * the guide does not answer, then those it is not sure it answers, each quoted as worded. A
 * sentence appears only when the check has a line of its kind, and a covered line is never named.
 * A line whose research question has no text to quote is left out, and with no line left there is
 * nothing to send: `null`. The check's reasons are not in it; Sigora reads them from the study.
 */
export const coverageFixMessage = (
  lines: readonly Pick<DgCoverageEntry, 'rq_id' | 'verdict'>[],
  rqText: ReadonlyMap<string, string>,
): string | null => {
  const textsOf = (verdict: 'gap' | 'uncertain') =>
    lines
      .filter((line) => line.verdict === verdict)
      .flatMap((line) => {
        const text = rqText.get(line.rq_id);

        return text === undefined ? [] : [text];
      });
  const gaps = textsOf('gap');
  const uncertain = textsOf('uncertain');
  if (gaps.length === 0 && uncertain.length === 0) {
    return null;
  }

  return [
    'Help me fix this.',
    gaps.length === 0 ? null : `The check says the guide doesn't answer: ${quotedList(gaps)}.`,
    uncertain.length === 0 ? null : `It isn't sure the guide answers: ${quotedList(uncertain)}.`,
    'Change the guide so it answers each of them, and keep my wording where you can.',
  ]
    .filter((sentence) => sentence !== null)
    .join(' ');
};
